import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchaddComponentDetails } from './component-details-apis';

// Row props interface (Component)
export interface Component {
  shipmentId: string;
  productId: string;
  componentId: string;
  tcId: string;
  tcAssociatedProductNo: string;
  tcProductComponentOrder: string;
  tcProductComponentNo: string;
  tcComponentDetail: string;
  tcComponentNetShippingWeight: number;
  tcComponentSupplementaryWeight: number;
}

// RenderDetailPanel props interface
interface RenderDetailPanelProps {
  row: { original: Component };
}

// Column Definitions for Product Component Standard Information
const standardColumns = [
  {
    accessorKey: 'tcComponentStandardLabelGrade',
    header: 'Standard Label Grade',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'tcComponentCertifiedWeight',
    header: 'Certified Weight (kg)',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'tcComponentStandardLabelGradeDescription', 
    header: 'Standard Label Grade Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

// Column Definitions for Component Raw Material Information
const rawMaterialColumns = [
  {
    accessorKey: 'tcComponentRawMaterialCode',
    header: 'Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'tcComponentRawMaterialPercentage',
    header: 'Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'tcComponentRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  // Extract keys from row data
  const { shipmentId, productId, componentId, tcId } = row.original;

  const { loading, executeGet } = useApi();
  const [componentData, setComponentData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
console.log(componentData);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!shipmentId || !productId || !componentId || !tcId || hasFetchedRef.current)
      return;
    hasFetchedRef.current = true;

    // API call using updated parameters
    fetchaddComponentDetails(executeGet, tcId, shipmentId, productId, componentId)
      .then((data) => setComponentData(data?.data))
      .catch((err) => setError(err.message));
  }, [shipmentId, productId, componentId, tcId, executeGet]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!componentData) {
    return <Typography>Loading component details...</Typography>;
  }

  // Using the updated accessor keys from your API response
  const standardInfo = componentData?.tcComponentInformation[0].tcProductComponentStandardInformation || [];
  const rawMaterialInfo = componentData?.tcComponentInformation[0].tcComponentRawMaterialInformation || [];
console.log(standardInfo,rawMaterialInfo)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Product Component Standard Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
          Product Component Standard Information
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableRowActions={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
          Raw Material Composition
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableRowActions={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
